import { $convertToMarkdownString, Transformer } from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isTableNode } from '@lexical/table';
import { $getRoot, $nodesOfType, LexicalEditor } from 'lexical';
import { useEffect, useRef } from 'react';
import { MarkdownImage } from 'src/company/templates/hooks/useConvertToMarkdownMutation';
import { createTransformers } from '../config/transformers';
import {
  $dismissSuggestion,
  $removeSuggestionWithContent,
  $setSuggestionSource,
  $sourceSuggestion,
  SuggestionNode,
} from '../nodes/SuggestionNode';
import {
  $removeEmptyListItems,
  $removeEmptyTableColumns,
  $removeEmptyTableRows,
  $setEditorValue,
  replaceImagesWithPlaceholders,
} from '../utils';
import { findScrollContainer } from '../utils/findScrollContainer';
import { isHtmlOrigin, markAsHtmlOrigin } from '../utils/tableOrigin';

export type EditorHelpers = {
  getContent: () => string;
  setContent: (markdown: string, onComplete?: () => void) => void;
  applyInlineSuggestions: () => void;
  discardSuggestions: () => void;
  setEditable: (editable: boolean) => void;
};

type EditorReadyPluginProps = {
  onEditorReady?: (helpers: EditorHelpers) => void;
  placeholderImages: MarkdownImage[];
};

function $collectHtmlOriginTableIndices(): Set<number> {
  const indices = new Set<number>();
  let tableIdx = 0;
  for (const child of $getRoot().getChildren()) {
    if ($isTableNode(child)) {
      if (isHtmlOrigin(child)) indices.add(tableIdx);
      tableIdx++;
    }
  }
  return indices;
}

function $restoreHtmlOriginMarkers(indices: Set<number>): void {
  if (indices.size === 0) return;
  let tableIdx = 0;
  for (const child of $getRoot().getChildren()) {
    if ($isTableNode(child)) {
      if (indices.has(tableIdx)) markAsHtmlOrigin(child);
      tableIdx++;
    }
  }
}

function preserveScroll(editor: LexicalEditor, afterUpdate: () => void): void {
  const scrollContainer = findScrollContainer(editor.getRootElement());
  const scrollTop = scrollContainer.scrollTop;

  afterUpdate();

  requestAnimationFrame(() => {
    scrollContainer.scrollTop = scrollTop;
  });
}

function $cleanOrphanHeadingsAndRenumber(transformers: Transformer[]): void {
  const original = $convertToMarkdownString(transformers, undefined, true);
  const cleaned = original
    .replace(/^#+[^\S\n]*\d+(?:\.\d+)*\.[^\S\n]*$/gm, '')
    .replace(/\n{3,}/g, '\n\n');
  let counter = 0;
  const renumbered = cleaned.replace(
    /^(#+[^\S\n]*)(\d+(?:\.\d+)*)(\.[^\S\n]+.+)$/gm,
    (_match, prefix, _number, suffix) => `${prefix}${++counter}${suffix}`,
  );
  if (renumbered !== original) {
    $setEditorValue(renumbered, transformers);
  }
}

export function EditorReadyPlugin({ onEditorReady, placeholderImages }: EditorReadyPluginProps) {
  const [editor] = useLexicalComposerContext();
  const transformers = createTransformers(editor);
  const onEditorReadyRef = useRef(onEditorReady);
  const placeholderImagesRef = useRef(placeholderImages);

  useEffect(() => {
    onEditorReadyRef.current = onEditorReady;
  }, [onEditorReady]);

  useEffect(() => {
    placeholderImagesRef.current = placeholderImages;
  }, [placeholderImages]);

  useEffect(() => {
    const callback = onEditorReadyRef.current;
    if (!callback) return;

    const getContent = (): string => {
      let content = '';
      editor.getEditorState().read(() => {
        const markdown = $convertToMarkdownString(transformers, undefined, true);
        content = replaceImagesWithPlaceholders(markdown, placeholderImagesRef.current);
      });
      return content;
    };

    const setContent = (markdown: string, onComplete?: () => void): void => {
      const scrollContainer = findScrollContainer(editor.getRootElement());
      const scrollTop = scrollContainer.scrollTop;

      editor.update(() => {
        $setEditorValue(markdown, transformers);
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!onComplete) {
            scrollContainer.scrollTop = scrollTop;
          }
          onComplete?.();
        });
      });
    };

    const applyInlineSuggestions = (): void => {
      editor.update(() => {
        for (const node of $nodesOfType(SuggestionNode)) {
          const source = $sourceSuggestion(node);
          if (source === 'inline') {
            $setSuggestionSource(node, 'default');
          } else if (source === 'deletion') {
            $removeSuggestionWithContent(node);
          }
        }
        $removeEmptyListItems($getRoot());
        $removeEmptyTableRows();
        $removeEmptyTableColumns();

        // Clean orphan headings and renumber within the same update so
        // the SuggestionTrackerPlugin mutation listener sees a single
        // consistent set of node mutations.
        $cleanOrphanHeadingsAndRenumber(transformers);
      });
    };

    const discardSuggestions = (): void => {
      preserveScroll(editor, () => {
        editor.update(() => {
          for (const node of $nodesOfType(SuggestionNode)) {
            const source = $sourceSuggestion(node);
            if (source === 'inline') {
              $removeSuggestionWithContent(node);
            } else if (source === 'deletion') {
              $dismissSuggestion(node);
            }
          }

          const htmlOriginIndices = $collectHtmlOriginTableIndices();

          const markdown = $convertToMarkdownString(transformers, undefined, true);
          $setEditorValue(markdown, transformers);

          $restoreHtmlOriginMarkers(htmlOriginIndices);
        });
      });
    };

    const setEditable = (editable: boolean): void => {
      editor.setEditable(editable);
    };

    callback({
      getContent,
      setContent,
      applyInlineSuggestions,
      discardSuggestions,
      setEditable,
    });
  }, [editor, transformers]);

  return null;
}
